/**
 * useDocumentEdgeDetection
 *
 * Lightweight real-time document edge detection for camera feeds.
 * Uses canvas-based image processing (no ML model required) to detect
 * the document boundary.
 *
 * Algorithm (downscale + brightness profile):
 *  1. Sample the video frame to a VERY small offscreen canvas (80×N).
 *     At this scale, text disappears (averaged out) but the document
 *     boundary — a large-scale bright-to-dark transition — survives.
 *  2. Compute the mean brightness per column (1D projection).
 *     The document (white paper) creates a region of high mean brightness.
 *     The background creates a region of lower mean brightness.
 *  3. Compute the mean brightness per row (1D projection).
 *  4. Apply Otsu's threshold on each 1D profile to find the split between
 *     bright (document) and dark (background).
 *  5. The document boundaries are where the profile crosses the threshold.
 *  6. Temporal EMA smoothing + jump rejection to prevent jitter.
 *
 * Why this works:
 * - At 80px wide, each pixel averages ~9 source pixels (for 720px video).
 *   Text lines are ~2-3px tall at this scale and get averaged into the
 *   white paper background, disappearing.
 * - The document boundary is a large-scale feature (the entire paper edge)
 *   that survives downscaling.
 * - 1D Otsu on the projection is far more robust than 2D Otsu because
 *   we're thresholding averages (which have much higher SNR than individual
 *   pixels).
 *
 * @author TotalAccess Development Team
 * @date July 2026
 */

import { useState, useRef, useEffect, useCallback } from 'react';

export interface DocumentEdge {
  /** Bounding box in relative coordinates (0–1) of the video viewport */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Detection confidence 0–1 */
  confidence: number;
  /** Aspect ratio of the detected document (width / height) */
  aspectRatio: number;
  /**
   * Per-side edge visibility: true when a real brightness transition was
   * detected on that side AND the boundary sits inside the frame with
   * margin — meaning the document ends in view there. false when the side
   * fell back to a full-extent default (top/bottom of long documents
   * extending past the frame) or the boundary is at the frame edge
   * (document likely continues beyond view). Left/right are only false
   * when the detected column bound lands at the frame edge.
   */
  sides?: { top: boolean; bottom: boolean; left: boolean; right: boolean };
}

export interface UseDocumentEdgeDetectionConfig {
  /** Sampling interval in ms (default: 120 — ~8fps, snappy tracking) */
  intervalMs?: number;
  /**
   * Downscale width for processing (default: 80).
   * Small enough that text disappears, large enough to locate the document.
   */
  sampleWidth?: number;
  /**
   * Smoothing factor for temporal EMA (0–1).
   * (default: 0.5 — responsive, tracks the document with minimal lag)
   */
  smoothing?: number;
  /**
   * Maximum allowed jump between consecutive detections (as fraction of frame).
   * (default: 0.2)
   */
  maxJump?: number;
  /** Whether detection is enabled (default: true) */
  enabled?: boolean;
}

interface SmoothedBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const INITIAL_SMOOTHED: SmoothedBox | null = null;

/**
 * Compute Otsu's threshold on a 1D signal.
 * Finds the threshold value that maximizes between-class variance,
 * separating "bright" (document) from "dark" (background) values.
 *
 * @param signal Float32Array of values (e.g., mean brightness per column)
 * @returns Optimal threshold value
 */
function otsu1D(signal: Float32Array): number {
  const n = signal.length;
  if (n < 2) return 0;

  // Find min/max to build histogram
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i++) {
    if (signal[i] < min) min = signal[i];
    if (signal[i] > max) max = signal[i];
  }
  if (max <= min) return min;

  // Build histogram (256 bins, scaled to [min, max])
  const bins = 256;
  const histogram = new Uint32Array(bins);
  const scale = (bins - 1) / (max - min);
  for (let i = 0; i < n; i++) {
    const bin = Math.round((signal[i] - min) * scale);
    histogram[bin]++;
  }

  // Otsu's method on the histogram
  let totalSum = 0;
  for (let t = 0; t < bins; t++) {
    totalSum += t * histogram[t];
  }

  let maxVariance = 0;
  let bestBin = 128;
  let sumBackground = 0;
  let weightBackground = 0;

  for (let t = 0; t < bins; t++) {
    weightBackground += histogram[t];
    if (weightBackground === 0) continue;

    const weightForeground = n - weightBackground;
    if (weightForeground === 0) break;

    sumBackground += t * histogram[t];

    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (totalSum - sumBackground) / weightForeground;

    const variance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

    if (variance > maxVariance) {
      maxVariance = variance;
      bestBin = t;
    }
  }

  // Convert bin back to signal value
  return min + bestBin / scale;
}

/**
 * Find document boundaries from a 1D brightness profile using gradient
 * (derivative) analysis.
 *
 * The document edge is where brightness changes most rapidly — a strong
 * transition from dark background to bright paper (or vice versa).
 *
 * Algorithm:
 *  1. Smooth the profile with a moving average (reduces noise)
 *  2. Compute the derivative (gradient) of the smoothed profile
 *  3. Find the strongest positive gradient (rising edge = left/top boundary)
 *  4. Find the strongest negative gradient (falling edge = right/bottom boundary)
 *  5. Validate: the region between the edges must be brighter than outside
 *
 * @param profile 1D brightness profile (mean brightness per column or row)
 * @param smoothRadius Moving average radius (default 2 = 5-tap). Larger values
 *                     suppress more noise but blur sharp edges.
 * @param minContrast Minimum inside/outside brightness ratio (default 1.10 = 10%).
 *                    Rejects flat / noisy scenes where there is no real document
 *                    edge — prevents the bounding box from "hunting" when the
 *                    camera points at a uniform surface.
 * @param minGradient Minimum absolute derivative magnitude for the strongest
 *                    rising/falling edge (default 8 on a 0–255 scale). If even
 *                    the sharpest edge is weaker than this, the profile is
 *                    considered featureless and null is returned.
 * @returns { start, end, threshold } or null
 */
function findBoundariesFromProfile(
  profile: Float32Array,
  smoothRadius = 2,
  minContrast = 1.05,
  minGradient = 4,
): { start: number; end: number; threshold: number } | null {
  const n = profile.length;
  if (n < 8) return null;

  // Step 1: Smooth the profile with a moving average
  const smoothed = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - smoothRadius); j <= Math.min(n - 1, i + smoothRadius); j++) {
      sum += profile[j];
      count++;
    }
    smoothed[i] = sum / count;
  }

  // Step 2: Compute derivative (central difference)
  const deriv = new Float32Array(n);
  for (let i = 1; i < n - 1; i++) {
    deriv[i] = smoothed[i + 1] - smoothed[i - 1];
  }
  deriv[0] = smoothed[1] - smoothed[0];
  deriv[n - 1] = smoothed[n - 1] - smoothed[n - 2];

  // Step 3: Find the strongest rising edge (positive derivative).
  const searchLimit = Math.floor(n * 0.7);
  let maxRise = 0;
  let leftEdge = -1;
  for (let i = 1; i < searchLimit; i++) {
    if (deriv[i] > maxRise) {
      maxRise = deriv[i];
      leftEdge = i;
    }
  }

  // Step 4: Find the strongest falling edge (negative derivative).
  const searchStart = Math.floor(n * 0.3);
  let maxFall = 0;
  let rightEdge = -1;
  for (let i = searchStart; i < n - 1; i++) {
    if (-deriv[i] > maxFall) {
      maxFall = -deriv[i];
      rightEdge = i;
    }
  }

  if (leftEdge < 0 || rightEdge < 0 || rightEdge <= leftEdge) return null;

  // Reject featureless profiles: if even the strongest edge is too weak,
  // there is no real document boundary — just noise. This is the primary
  // guard against the bounding box "hunting" on uniform backgrounds.
  if (maxRise < minGradient || maxFall < minGradient) return null;

  // Step 5: Validate — the region between the edges should be brighter
  // than the region outside.
  let insideSum = 0;
  for (let i = leftEdge; i <= rightEdge; i++) insideSum += smoothed[i];
  const insideMean = insideSum / (rightEdge - leftEdge + 1);

  let outsideSum = 0;
  let outsideCount = 0;
  for (let i = 0; i < leftEdge; i++) { outsideSum += smoothed[i]; outsideCount++; }
  for (let i = rightEdge + 1; i < n; i++) { outsideSum += smoothed[i]; outsideCount++; }
  const outsideMean = outsideCount > 0 ? outsideSum / outsideCount : 0;

  // The document region must be brighter than the background
  if (outsideMean > 0 && insideMean < outsideMean * minContrast) return null;

  const threshold = (insideMean + outsideMean) / 2;

  return { start: leftEdge, end: rightEdge, threshold };
}

export function useDocumentEdgeDetection(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  config: UseDocumentEdgeDetectionConfig = {},
) {
  const {
    intervalMs = 120,
    sampleWidth = 80,
    smoothing = 0.5,
    maxJump = 0.2,
    enabled = true,
  } = config;

  const [documentEdge, setDocumentEdge] = useState<DocumentEdge | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const smoothedRef = useRef<SmoothedBox | null>(INITIAL_SMOOTHED);
  const rafRef = useRef<number | null>(null);
  const lastSampleRef = useRef<number>(0);
  const enabledRef = useRef(enabled);
  const configRef = useRef({ smoothing, maxJump, sampleWidth });
  // Tracks consecutive detection misses. After a few misses in a row, the
  // bounding box is cleared immediately rather than fading slowly — this
  // prevents the box from lingering/hunting when the document is removed.
  const consecutiveMissesRef = useRef<number>(0);

  useEffect(() => {
    enabledRef.current = enabled;
    configRef.current = { smoothing, maxJump, sampleWidth };
  }, [enabled, smoothing, maxJump, sampleWidth]);

  const detectEdge = useCallback((): DocumentEdge | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return null;

    const { sampleWidth: sWidth } = configRef.current;
    const sHeight = Math.round(sWidth * (video.videoHeight / video.videoWidth));

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    canvas.width = sWidth;
    canvas.height = sHeight;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    try {
      // drawImage with downscaling automatically averages pixels,
      // which is exactly the heavy blur we want.
      ctx.drawImage(video, 0, 0, sWidth, sHeight);
    } catch {
      return null;
    }

    let imageData: ImageData;
    try {
      imageData = ctx.getImageData(0, 0, sWidth, sHeight);
    } catch {
      return null;
    }

    const data = imageData.data;
    const totalPixels = sWidth * sHeight;

    // Step 1: Convert to grayscale
    const gray = new Uint8ClampedArray(totalPixels);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
      gray[j] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    }

    // Step 2: Compute mean brightness per column (column profile).
    // This averages over ALL rows, so the document's bright columns stand out.
    const colProfile = new Float32Array(sWidth);
    for (let x = 0; x < sWidth; x++) {
      let sum = 0;
      for (let y = 0; y < sHeight; y++) {
        sum += gray[y * sWidth + x];
      }
      colProfile[x] = sum / sHeight;
    }

    // Step 3: Find column boundaries first (left/right edges of document).
    // The column profile has a clear bright region (document) against
    // darker background, so the derivative approach works well here.
    const colBounds = findBoundariesFromProfile(colProfile);

    if (!colBounds) return null;

    let leftX = colBounds.start;
    let rightX = colBounds.end;

    // Step 4: Compute the row profile using ONLY the columns within the
    // detected document boundaries. This is the key insight:
    //
    // If we compute the row profile across ALL columns, the dark background
    // on either side of the document dilutes the document's brightness,
    // making the top/bottom edges hard to detect.
    //
    // By restricting to the document's column range, the row profile
    // reflects only the document's brightness variation, making the
    // top/bottom transitions much sharper.
    const colStart = Math.max(0, Math.round(leftX));
    const colEnd = Math.min(sWidth - 1, Math.round(rightX));
    const colRange = colEnd - colStart + 1;

    const rowProfileRestricted = new Float32Array(sHeight);
    for (let y = 0; y < sHeight; y++) {
      let sum = 0;
      const rowOffset = y * sWidth;
      for (let x = colStart; x <= colEnd; x++) {
        sum += gray[rowOffset + x];
      }
      rowProfileRestricted[y] = sum / colRange;
    }

    // Also compute the full-width row profile as a fallback
    const rowProfileFull = new Float32Array(sHeight);
    for (let y = 0; y < sHeight; y++) {
      let sum = 0;
      const rowOffset = y * sWidth;
      for (let x = 0; x < sWidth; x++) {
        sum += gray[rowOffset + x];
      }
      rowProfileFull[y] = sum / sWidth;
    }

    // Step 5: Find row boundaries using multiple strategies.
    // Thresholds are lowered for daytime/low-contrast scenes where a white
    // receipt on a bright desk has minimal brightness difference.
    // `rowBoundsDetected` tracks whether a REAL row transition was found —
    // strategies 5 and the partial-edge fallback below invent full-height
    // bounds for documents extending past the frame and must not count.
    let rowBoundsDetected = false;
    // Strategy 1: Restricted row profile with light smoothing
    let rowBounds = findBoundariesFromProfile(rowProfileRestricted, 2, 1.04);

    // Strategy 2: Restricted row profile with heavier smoothing
    if (!rowBounds) {
      rowBounds = findBoundariesFromProfile(rowProfileRestricted, 5, 1.03);
    }

    // Strategy 3: Full-width row profile (the background on either side
    // actually helps create a stronger top/bottom transition in some cases)
    if (!rowBounds) {
      rowBounds = findBoundariesFromProfile(rowProfileFull, 3, 1.04);
    }

    // Strategy 4: Full-width with heavier smoothing
    if (!rowBounds) {
      rowBounds = findBoundariesFromProfile(rowProfileFull, 6, 1.03);
    }

    rowBoundsDetected = !!rowBounds;

    // Strategy 5: If all strategies fail, the document likely fills most of
    // the frame vertically. Use a generous default (top 5% to bottom 95%).
    // IMPORTANT: Only trigger when the document region is both flat (low
    // variance) AND bright (a white receipt/paper filling the frame). A flat
    // but dark profile is just a uniform background (wall, desk, floor) with
    // no document — triggering here would invent a phantom bounding box.
    if (!rowBounds) {
      let rMin = Infinity, rMax = -Infinity;
      for (let i = 0; i < sHeight; i++) {
        if (rowProfileRestricted[i] < rMin) rMin = rowProfileRestricted[i];
        if (rowProfileRestricted[i] > rMax) rMax = rowProfileRestricted[i];
      }
      const range = rMax - rMin;
      // Flat (range < 30) AND bright (darkest column in the doc region > 100)
      // — a white sheet filling the frame. Lowered from 120 to 100 for
      // daytime scenes where ambient light reduces overall brightness.
      // Reject dark/flat scenes (dark uniform background with no document).
      if (range < 30 && rMin > 100) {
        rowBounds = { start: sHeight * 0.05, end: sHeight * 0.95, threshold: (rMin + rMax) / 2 };
      }
    }

    // NOTE: Per-frame console.log removed — it was a significant perf hit
    // on mobile (stringify + log I/O every 200ms). Use the debugLogger
    // with a verbose flag if profiling is needed.

    // Partial-edge fallback for long documents (e.g. till slips that extend
    // above and below the frame). When column edges (left/right) are
    // detected but row edges (top/bottom) are not, the document likely
    // fills the entire vertical extent of the frame. In this case, use the
    // full frame height (with a small margin) so the bounding box still
    // tracks the document horizontally and auto-capture can fire.
    if (!rowBounds) {
      // Check if the document region (between leftX and rightX) is
      // uniformly bright — indicating the slip fills the frame vertically.
      let docMean = 0;
      let docCount = 0;
      for (let y = 0; y < sHeight; y++) {
        const rowOffset = y * sWidth;
        for (let x = colStart; x <= colEnd; x++) {
          docMean += gray[rowOffset + x];
          docCount++;
        }
      }
      docMean /= Math.max(1, docCount);

      // Also check the background brightness to ensure there IS contrast
      // horizontally (the slip is brighter than the background on the sides).
      let bgMean = 0;
      let bgCount = 0;
      for (let y = 0; y < sHeight; y++) {
        const rowOffset = y * sWidth;
        for (let x = 0; x < colStart; x++) { bgMean += gray[rowOffset + x]; bgCount++; }
        for (let x = colEnd + 1; x < sWidth; x++) { bgMean += gray[rowOffset + x]; bgCount++; }
      }
      bgMean /= Math.max(1, bgCount);

      // If the document region is bright (>100) and brighter than the
      // background (at least 5% brighter), accept it as a full-height
      // document (long slip extending beyond the frame).
      if (docMean > 100 && bgMean > 0 && docMean >= bgMean * 1.05) {
        rowBounds = { start: 0, end: sHeight - 1, threshold: (docMean + bgMean) / 2 };
      }
    }

    if (!rowBounds) return null;

    let topY = rowBounds.start;
    let bottomY = rowBounds.end;

    // Step 5: Validate the detected box.
    const boxW = rightX - leftX;
    const boxH = bottomY - topY;

    // Reject boxes that are too small
    if (boxW < sWidth * 0.15 || boxH < sHeight * 0.15) return null;

    // Reject boxes that are too large (no document, just bright scene).
    // Allow one dimension to fill 95%+ (long slips extending beyond frame),
    // but reject if BOTH dimensions fill 95%+ (no document edge visible).
    if (boxW > sWidth * 0.95 && boxH > sHeight * 0.95) return null;

    // Step 6: Compute confidence from the RAW detected boundaries (before
    // margin expansion). Computing confidence after the margin would include
    // background pixels in the "document" region, artificially lowering the
    // contrast and confidence.
    const rawW = boxW;
    const rawH = boxH;
    const docColMean = colProfile.slice(Math.round(leftX), Math.round(rightX) + 1)
      .reduce((a, b) => a + b, 0) / Math.max(1, rawW);
    const bgColMean = (colProfile.slice(0, Math.round(leftX)).reduce((a, b) => a + b, 0) +
      colProfile.slice(Math.round(rightX) + 1).reduce((a, b) => a + b, 0)) /
      Math.max(1, sWidth - rawW);
    const docRowMean = rowProfileRestricted.slice(Math.round(topY), Math.round(bottomY) + 1)
      .reduce((a, b) => a + b, 0) / Math.max(1, rawH);
    const bgRowMean = (rowProfileRestricted.slice(0, Math.round(topY)).reduce((a, b) => a + b, 0) +
      rowProfileRestricted.slice(Math.round(bottomY) + 1).reduce((a, b) => a + b, 0)) /
      Math.max(1, sHeight - rawH);

    const colContrast = bgColMean > 0 ? (docColMean - bgColMean) / bgColMean : 0;
    const rowContrast = bgRowMean > 0 ? (docRowMean - bgRowMean) / bgRowMean : 0;
    // When the document fills the full height (long slip extending beyond
    // frame), bgRowMean is 0 and rowContrast is 0. In this case, rely on
    // the column contrast alone — it's the only signal available, and it's
    // sufficient because the left/right edges are clearly detected.
    const confidence = rowContrast === 0
      ? Math.min(1, colContrast * 3)
      : Math.min(1, ((colContrast + rowContrast) / 2) * 3);

    // Record which document sides were confirmed inside the frame by a real
    // brightness transition — computed from the RAW bounds, before the +10%
    // margin expansion below pushes them outward. A boundary sitting at the
    // frame edge cannot prove the document ends there (it may continue
    // beyond view), so it does not count as visible. Column bounds are
    // always genuinely detected when we reach this point (the detector
    // returns null otherwise); row bounds use the strategies-1-4 flag.
    const SIDE_INSET = 0.02;
    const sides = {
      top: rowBoundsDetected && topY / sHeight >= SIDE_INSET,
      bottom: rowBoundsDetected && bottomY / sHeight <= 1 - SIDE_INSET,
      left: leftX / sWidth >= SIDE_INSET,
      right: rightX / sWidth <= 1 - SIDE_INSET,
    };

    // Step 7: Expand the box outward to compensate for the systematic inward
    // bias of gradient-based edge detection. The moving-average smoothing
    // (5-tap on an 80px image) and the gradient peak-finding approach both
    // find positions inside the actual document edge. A 10% margin per side
    // compensates for this bias so the bounding box aligns with the true
    // document boundary.
    const marginX = sWidth * 0.10;
    const marginY = sHeight * 0.10;
    leftX = Math.max(0, leftX - marginX);
    topY = Math.max(0, topY - marginY);
    rightX = Math.min(sWidth - 1, rightX + marginX);
    bottomY = Math.min(sHeight - 1, bottomY + marginY);

    const finalW = rightX - leftX;
    const finalH = bottomY - topY;

    if (finalW < sWidth * 0.12 || finalH < sHeight * 0.12) return null;

    // Convert to relative coordinates (0–1)
    const relX = leftX / sWidth;
    const relY = topY / sHeight;
    const relW = finalW / sWidth;
    const relH = finalH / sHeight;

    return {
      x: relX,
      y: relY,
      width: relW,
      height: relH,
      confidence: isFinite(confidence) ? confidence : 0,
      aspectRatio: relW / relH,
      sides,
    };
  }, [videoRef]);

  const smoothBox = useCallback((raw: DocumentEdge): SmoothedBox => {
    const { smoothing: smooth, maxJump: jump } = configRef.current;
    const prev = smoothedRef.current;

    const rawBox: SmoothedBox = { x: raw.x, y: raw.y, width: raw.width, height: raw.height };

    if (!prev) {
      smoothedRef.current = rawBox;
      return rawBox;
    }

    const jumpX = Math.abs(raw.x - prev.x);
    const jumpY = Math.abs(raw.y - prev.y);
    const jumpW = Math.abs(raw.width - prev.width);
    const jumpH = Math.abs(raw.height - prev.height);
    if (jumpX > jump || jumpY > jump || jumpW > jump || jumpH > jump) {
      // Jump rejection: the raw detection jumped more than `maxJump` in a
      // single frame — likely a spurious detection or the document moved
      // quickly. Blend at 35% (not 10%) so the box catches up in ~3 frames
      // (~360ms at 120ms sampling) instead of ~10 frames (~1.2s). The old
      // 10% blend made the box feel sluggish when the document was moved.
      const slight = 0.35;
      const blended: SmoothedBox = {
        x: prev.x + (raw.x - prev.x) * slight,
        y: prev.y + (raw.y - prev.y) * slight,
        width: prev.width + (raw.width - prev.width) * slight,
        height: prev.height + (raw.height - prev.height) * slight,
      };
      smoothedRef.current = blended;
      return blended;
    }

    const blended: SmoothedBox = {
      x: prev.x + (raw.x - prev.x) * smooth,
      y: prev.y + (raw.y - prev.y) * smooth,
      width: prev.width + (raw.width - prev.width) * smooth,
      height: prev.height + (raw.height - prev.height) * smooth,
    };
    smoothedRef.current = blended;
    return blended;
  }, []);

  const tick = useCallback(() => {
    rafRef.current = requestAnimationFrame(tick);

    if (!enabledRef.current) return;

    const now = performance.now();
    if (now - lastSampleRef.current < intervalMs) return;
    lastSampleRef.current = now;

    const raw = detectEdge();
    if (raw) {
      consecutiveMissesRef.current = 0;
      const smoothed = smoothBox(raw);
      setDocumentEdge({
        x: smoothed.x,
        y: smoothed.y,
        width: smoothed.width,
        height: smoothed.height,
        confidence: raw.confidence,
        aspectRatio: smoothed.width / smoothed.height,
        sides: raw.sides,
      });
    } else {
      // No document detected. Instead of slowly fading the box (which made
      // it linger for ~4s and visibly "hunt" on no-document scenes), count
      // consecutive misses and clear the box after 2 misses (~240ms grace
      // period to absorb single-frame flicker). This makes the bounding box
      // disappear promptly when the document is removed or not in frame.
      consecutiveMissesRef.current += 1;
      if (consecutiveMissesRef.current >= 2) {
        smoothedRef.current = null;
        setDocumentEdge(null);
      }
    }
  }, [detectEdge, smoothBox, intervalMs]);

  useEffect(() => {
    if (!enabled) {
      setDocumentEdge(null);
      smoothedRef.current = null;
      consecutiveMissesRef.current = 0;
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, tick]);

  useEffect(() => {
    if (!enabled) {
      smoothedRef.current = null;
      consecutiveMissesRef.current = 0;
      setDocumentEdge(null);
    }
  }, [enabled]);

  return { documentEdge };
}
